import React, { Component } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { saveDocumentWithIdentifier } from "../actions/fs";

import FileTypes from "./editor/FileTypes";
import EditorTitle from "./editor/EditorTitle";

import SimpleTextEditor from "./editor/simpletext/SimpleTextEditor";
import ManuscriptEditor from "./editor/manuscript/ManuscriptEditor";

/**
 * Import editor base stylesheet
 */
import "./editor/EditorBase.css";

/**
 * Document : Component
 *
 * (c) Stefan Wittwer 2018 | Alle Rechte vorbehalten.
 *
 * Document viewer and editor module and controller
 */
class Document extends Component {
  state = {
    /**
     * If true, the editor will not permit any direct changes
     * to the document and go into viewer mode
     */
    isReadOnlyMode: false
  };

  /**
   * Dispatches a save of the new document title
   */
  saveDocumentTitle = title => {
    const file = this.currentFile();

    if (!title || title.trim() === "") return;

    file.filename = title;
    this.updateDocument(file);
  };

  /**
   * Returns true if the user is allowed to edit the currently open document
   */
  isFileEditable() {
    if (!this.isFileOpen()) return false;
    if (this.state.isReadOnlyMode) return false;

    const file = this.currentFile();
    const user = this.props.userIdentifier;

    if (file.creator === user) {
      return true;
    }

    return false;
  }

  /**
   * Returns true if there is currently a document open in the editor
   */
  isFileOpen() {
    return (
      this.props.currentContext != null &&
      this.props.currentDocument != null &&
      this.props.files.hasOwnProperty(this.props.currentContext)
    );
  }

  /**
   * Returns the currently open editor file or null
   */
  currentFile() {
    if (!this.isFileOpen()) return null;

    const filtered = this.props.files[this.props.currentContext].filter(
      item => item.id === this.props.currentDocument
    );
    if (filtered.length !== 1) {
      return null;
    }

    return JSON.parse(JSON.stringify(filtered[0]));
  }

  /**
   * Dispatches a document save using a new version of the file
   */
  updateDocument = updatedFile => {
    this.props.saveDocumentWithIdentifier(updatedFile.id, updatedFile);
  };

  /**
   * Provides an editor component for editing the specified file
   */
  editorForFile = file => {
    const editorProps = {
      file,
      updateDocument: this.updateDocument,
      isReadOnlyMode: !this.isFileEditable()
    };

    switch (file.documentType) {
      case FileTypes.SimpleText:
        return <SimpleTextEditor {...editorProps} />;
      case FileTypes.Manuscript:
        return <ManuscriptEditor {...editorProps} />;
      default:
        return (
          <FormattedMessage
            id="editor.message.unknownFileType"
            description="Error message when a file is opened with an unknown type"
          />
        );
    }
  };

  /**
   * Renders the document viewer/editor
   */
  render() {
    const file = this.currentFile();

    if (!file) {
      return (
        <div>
          <span className="ed-empty-state">
            <FormattedMessage
              id="editor.message.empty"
              description="Empty state message for no open editor documents"
            />
          </span>
        </div>
      );
    }

    return (
      <div className="ed-editor">
        <EditorTitle
          file={file}
          isReadOnlyMode={!this.isFileEditable()}
          saveDocumentTitle={this.saveDocumentTitle}
        />
        {this.editorForFile(file)}
      </div>
    );
  }
}

Document.propTypes = {
  /**
   * currentContext: String
   * Identifier for the currently selected context
   */
  currentContext: PropTypes.string,
  /**
   * currentDocument: String
   * Identifier for the currently open document
   */
  currentDocument: PropTypes.string,
  /**
   * files: Object
   * List of loaded files ordered by context
   */
  files: PropTypes.shape({}).isRequired,
  /**
   * Actions to dispatch
   */
  saveDocumentWithIdentifier: PropTypes.func.isRequired,
  /**
   * userIdentifier: String
   * Identifier of the currently logged in user
   */
  userIdentifier: PropTypes.string.isRequired
};

Document.defaultProps = {
  currentContext: null,
  currentDocument: null
};

const mapStateToProps = state => ({
  currentContext: state.tasklist.currentContext || null,
  currentDocument: state.tasklist.currentDocument || null,

  files: JSON.parse(JSON.stringify(state.fs.documents || {})),

  userIdentifier: state.user.uid || ""
});

export default connect(
  mapStateToProps,
  { saveDocumentWithIdentifier }
)(Document);



// WEBPACK FOOTER //
// ./src/modules/Document.js